/**
 * Canned assistant streams. The panel is built and demoed against these
 * (VITE_ASSISTANT_STUB=true) and the e2e suite serves the same frames as a
 * real text/event-stream body, so they are the shared truth between U1 and
 * C2. Numbers are illustrative. Keep them in step with the streaming
 * contract's "Turn lifecycle" example.
 */
import type { AssistantEvent, AssistantErrorCode } from "@/types/assistant";

/** One frame as it goes on the wire: `event:` name plus the `data:` JSON. */
export type FixtureFrame = {
  [E in AssistantEvent as E["name"]]: { event: E["name"]; data: Omit<E, "name"> };
}[AssistantEvent["name"]];

const TURN = "trn_01J9";
const THREAD = "thr_01J8";
const at = (ms: number) => new Date(Date.UTC(2026, 9, 29, 10, 14, 1, 900) + ms).toISOString();

/** "How did affiliate sales do last month versus the month before?" */
export const askTurn: FixtureFrame[] = [
  {
    event: "turn.start",
    data: {
      seq: 1, turnId: TURN, ts: at(0),
      threadId: THREAD, userMessageId: "msg_01",
      allowance: { used: 1, limit: 200, resetsAt: "2026-11-01T00:00:00Z" },
    },
  },
  {
    event: "tool.start",
    data: {
      seq: 2, turnId: TURN, ts: at(218),
      callId: "call_01", tool: "get_campaign_sales",
      label: "Looking up campaign sales for September and October",
      args: { from: "2026-09-01", to: "2026-10-31" },
    },
  },
  {
    event: "tool.end",
    data: {
      seq: 3, turnId: TURN, ts: at(630),
      callId: "call_01", status: "ok", summary: "Campaign sales for 2 months",
      asOf: "2026-10-29T10:14:02Z", deepLink: "/brand/analytics?range=2m", durationMs: 412,
    },
  },
  {
    event: "text.delta",
    data: { seq: 4, turnId: TURN, ts: at(1110), delta: "Affiliate sales were **$18,420** in October" },
  },
  {
    event: "text.delta",
    data: {
      seq: 5, turnId: TURN, ts: at(1220),
      delta:
        ", up 14% on September ($16,150). Two campaigns drove the rise:\n\n" +
        "| Campaign | September | October |\n| --- | --- | --- |\n" +
        "| FALL15 | $6,900 | $9,300 |\n| CREATOR10 | $4,100 | $5,250 |\n\n" +
        "The rest of the programs were flat. Orders per affiliate held at 3.1, so the growth came from more active affiliates, not bigger baskets.",
    },
  },
  {
    event: "turn.end",
    data: {
      seq: 6, turnId: TURN, ts: at(2500),
      assistantMessageId: "msg_02", stopReason: "end",
      usage: { inputTokens: 14210, outputTokens: 188, cacheReadTokens: 11900, costUsd: 0.0016 },
      allowance: { used: 1, limit: 200, resetsAt: "2026-11-01T00:00:00Z" },
    },
  },
];

/** "Create WELCOME20, 10% off all products, for new affiliates." */
export const doTurn: FixtureFrame[] = [
  {
    event: "turn.start",
    data: {
      seq: 1, turnId: "trn_01JA", ts: at(0),
      threadId: THREAD, userMessageId: "msg_03",
      allowance: { used: 2, limit: 200, resetsAt: "2026-11-01T00:00:00Z" },
    },
  },
  {
    event: "tool.start",
    data: {
      seq: 2, turnId: "trn_01JA", ts: at(300),
      callId: "call_02", tool: "create_product_campaign",
      label: "Drafting the WELCOME20 campaign",
      args: { code: "WELCOME20", percentOff: 10, appliesTo: "all_products", audience: "new_affiliates" },
    },
  },
  {
    event: "tool.end",
    data: {
      seq: 3, turnId: "trn_01JA", ts: at(720),
      callId: "call_02", status: "ok", summary: "Proposal ready, waiting for Confirm",
      asOf: null, deepLink: "/brand/campaigns", durationMs: 380,
    },
  },
  {
    event: "proposal",
    data: {
      seq: 4, turnId: "trn_01JA", ts: at(730),
      proposalId: "prp_01", tool: "create_product_campaign",
      title: "Create WELCOME20, 10% off",
      changes: [
        { field: "code", from: null, to: "WELCOME20" },
        { field: "discount", from: null, to: "10%" },
      ],
      warnings: [],
      // 15 minutes from replay time (ASSISTANT_PROPOSAL_TTL_MINUTES), never a
      // fixed date: a past date would render the card already expired.
      expiresAt: new Date(Date.now() + 15 * 60_000).toISOString(),
    },
  },
  {
    event: "text.delta",
    data: {
      seq: 5, turnId: "trn_01JA", ts: at(1300),
      delta: "Here's the campaign as I'd create it. Nothing is live until you confirm.",
    },
  },
  {
    event: "turn.end",
    data: {
      seq: 6, turnId: "trn_01JA", ts: at(1900),
      assistantMessageId: "msg_04", stopReason: "end",
      usage: { inputTokens: 15020, outputTokens: 96, cacheReadTokens: 11900, costUsd: 0.0014 },
      allowance: { used: 2, limit: 200, resetsAt: "2026-11-01T00:00:00Z" },
    },
  },
];

/** The 429 JSON body returned before any stream opens. */
export const allowanceExceeded: { status: number; code: AssistantErrorCode; message: string; retryable: boolean } = {
  status: 429,
  code: "ALLOWANCE_EXCEEDED",
  message: "You've used this month's 200 questions. Resets on 2026-11-01.",
  retryable: false,
};

/** Which fixture a stubbed message gets, so a demo can reach every state. */
export function pickFixture(message: string): "ask" | "do" | "allowance" {
  const m = message.toLowerCase();
  if (m.includes("allowance")) return "allowance";
  if (/\b(create|make|set up|add|turn (on|off)|invite|approve)\b/.test(m)) return "do";
  return "ask";
}

const sleep = (ms: number, signal?: AbortSignal) =>
  new Promise<void>((resolve, reject) => {
    const t = setTimeout(resolve, ms);
    signal?.addEventListener(
      "abort",
      () => {
        clearTimeout(t);
        reject(new DOMException("Aborted", "AbortError"));
      },
      { once: true },
    );
  });

/**
 * Replay a fixture as a live stream. Text deltas are split into words so the
 * streaming render is exercised the way the real backend exercises it.
 */
export async function* replay(
  frames: FixtureFrame[],
  { delayMs = 60, signal }: { delayMs?: number; signal?: AbortSignal } = {},
): AsyncGenerator<AssistantEvent> {
  for (const frame of frames) {
    await sleep(delayMs * 3, signal);
    if (frame.event === "text.delta") {
      const { delta, ...rest } = frame.data;
      const words = delta.split(/(?<=\s)/);
      for (const word of words) {
        await sleep(delayMs / 3, signal);
        yield { name: "text.delta", ...rest, delta: word };
      }
      continue;
    }
    yield { name: frame.event, ...frame.data } as AssistantEvent;
  }
}

/** Serialise frames the way the backend will, for route mocks and tests. */
export function toSseBody(frames: FixtureFrame[]): string {
  return frames
    .map((f) => `event: ${f.event}\nid: ${f.data.seq}\ndata: ${JSON.stringify(f.data)}\n\n`)
    .join("");
}

/** 200 body of POST /assistant/proposals/prp_01/confirm, for stubs and route mocks. */
export const proposalConfirmed = {
  proposalId: "prp_01",
  status: "CONFIRMED" as const,
  summary: "WELCOME20 is live. New affiliates can use it now.",
  deepLink: "/brand/campaigns",
};

/** 200 body of POST /assistant/proposals/prp_01/cancel. */
export const proposalCancelled = { proposalId: "prp_01", status: "CANCELLED" as const };

/** 410 body when Confirm comes after expiresAt. */
export const proposalExpired = {
  status: 410,
  code: "PROPOSAL_EXPIRED" as const,
  message: "This change expired. Ask the assistant again to get a fresh one.",
  retryable: false,
};
